import { useMemo } from "react";
import { useAtom } from "jotai";
import { useAuth } from "../../context/AuthContext";
import { isInternalRole } from "../../services/roles";
import { reviewerDisplayModeAtom } from "../../atoms/reviewerPrefsAtoms";
import type { NativePredictions } from "../../types/predictions";
import type { FindingJumpTarget } from "../../hooks/useReportQuery";
import PrototypeReport from "../PrototypeReport";
import ReviewerPanel from "./ReviewerPanel";
import { ReviewerControlBar } from "./ReviewerControlBar";
import { ReviewerSettingsMenu } from "./ReviewerSettingsMenu";

interface Props {
  reviewCohortId?: string;
  studyUid: string;
  /** Composite `uid::jobId` for PrototypeReport (job attribution / autosave). */
  reportStudyId: string;
  predictions: NativePredictions;
  /** List mode: ReviewerPanel jumps by top-slice UID. */
  onFindingClick?: (topSliceUid: string) => void;
  /** Inline mode: PrototypeReport jumps by a rich finding target. */
  onInlineFindingClick?: (target: FindingJumpTarget) => void;
}

/**
 * Reviewer case surface. Inline = the reporter inline UX + right-click verdicts,
 * with the disposition bar (ReviewerControlBar) below. List = the classic
 * ReviewerPanel. Both bottoms share the same disposition layout and both carry
 * the Configure gear (ReviewerSettingsMenu) on the right so the reviewer can
 * flip modes from either view.
 */
export function ReviewerReportShell({
  reviewCohortId,
  studyUid,
  reportStudyId,
  predictions,
  onFindingClick,
  onInlineFindingClick,
}: Props) {
  const [mode, setMode] = useAtom(reviewerDisplayModeAtom);
  const { userType } = useAuth();
  // Derive the capability from role instead of hardcoding — mirrors the
  // reporter's settings menu (PrototypeReport). Reviewers keep templates.
  const canEditTemplates = isInternalRole(userType) || userType === "reviewer";

  const reviewVerdictArg = useMemo(
    () => (reviewCohortId ? { cohortId: reviewCohortId, studyUid } : undefined),
    [reviewCohortId, studyUid],
  );

  const inline = mode === "inline" && reviewCohortId;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1 overflow-hidden">
        {inline ? (
          <PrototypeReport
            studyId={reportStudyId}
            sessionMode="demo"
            templateMode="ai"
            onFindingClick={onInlineFindingClick}
            reviewVerdict={reviewVerdictArg}
            hideOriginalReport
          />
        ) : (
          <ReviewerPanel
            reviewCohortId={reviewCohortId}
            studyUid={studyUid}
            predictions={predictions}
            onFindingClick={onFindingClick}
            modeToggle={
              reviewCohortId ? (
                <ReviewerSettingsMenu
                  mode={mode}
                  onChangeMode={setMode}
                  canEditTemplates={canEditTemplates}
                />
              ) : undefined
            }
          />
        )}
      </div>
      {inline && (
        <ReviewerControlBar
          mode={mode}
          onChangeMode={setMode}
          cohortId={reviewCohortId}
          studyUid={studyUid}
          canEditTemplates={canEditTemplates}
        />
      )}
    </div>
  );
}
